import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { CheckUserV2RequestViewModel } from '../models/CheckUserV2RequestViewModel';
import type { EmptyResponseViewModel } from '../models/EmptyResponseViewModel';
import type { EtkViewModel } from '../models/EtkViewModel';
import type { MyUserGetViewModel } from '../models/MyUserGetViewModel';
import type { MyUserUpsertViewModel } from '../models/MyUserUpsertViewModel';
import type { SalesMasterViewModel } from '../models/SalesMasterViewModel';
import type { UnregisteredInternationalShoppingRequestViewModel } from '../models/UnregisteredInternationalShoppingRequestViewModel';
import type { UnregisteredInternationalShoppingViewModel } from '../models/UnregisteredInternationalShoppingViewModel';
import type { UserUpdateV2ViewModel } from '../models/UserUpdateV2ViewModel';
import type { UserUpsertRequestViewModel } from '../models/UserUpsertRequestViewModel';
import type { UserV2ViewModel } from '../models/UserV2ViewModel';
import type { WebChatPermissionsRequestViewModel } from '../models/WebChatPermissionsRequestViewModel';
import type { WebChatPermissionsViewModel } from '../models/WebChatPermissionsViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class UsersService {
  /**
   * Profile
   * Gets profile by token
   * @returns UserV2ViewModel Success
   * @throws ApiError
   */
  public static async getUser(
    {
      authorization,
      withClubs = false,
    }: {
      /**
       * Authorization
       */
      authorization: string;
      withClubs?: boolean;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<UserV2ViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<UserV2ViewModel>({
      method: 'GET',
      url: `/v2/users`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(withClubs ? { withClubs: withClubs } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Profile
   * Updates profile by token
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async updateUser(
    {
      authorization,
      body,
    }: {
      /**
       * Authorization
       */
      authorization: string;
      body?: UserUpdateV2ViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'PUT',
      url: `/v2/users`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Profile
   * Deletes profile by token
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async deleteUser(
    {
      authorization,
    }: {
      /**
       * Authorization
       */
      authorization: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'DELETE',
      url: `/v2/users`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Reservations
   * Gets all reservations by token
   * @returns SalesMasterViewModel Success
   * @throws ApiError
   */
  public static async listReservations(
    {
      authorization,
      page,
      size,
    }: {
      /**
       * Authorization
       */
      authorization: string;
      page?: number;
      size?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<SalesMasterViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<Array<SalesMasterViewModel>>({
      method: 'GET',
      url: `/v2/users/reservations`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(page ? { page: page } : {}),
        ...(size ? { size: size } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Update ETK Settings
   * Updates etk settings
   * @returns EtkViewModel Success
   * @throws ApiError
   */
  public static async updateEtk(
    {
      authorization,
      body,
    }: {
      /**
       * Authorization
       */
      authorization?: string;
      body?: UserUpsertRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EtkViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EtkViewModel>({
      method: 'PUT',
      url: `/v2/users/etk`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Check User with Loyalty
   * Gets users membership states
   * @returns string Success
   * @throws ApiError
   */
  public static async checkUser(
    {
      body,
    }: {
      body?: CheckUserV2RequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<string> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<string>({
      method: 'POST',
      url: `/v2/users/check`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Push Permissions
   * Pushes permissions for unregistered users with international shopping
   * @returns UnregisteredInternationalShoppingViewModel Success
   * @throws ApiError
   */
  public static async pushPermissions(
    {
      body,
    }: {
      body?: UnregisteredInternationalShoppingRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<UnregisteredInternationalShoppingViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<UnregisteredInternationalShoppingViewModel>({
      method: 'POST',
      url: `/v2/users/push/unregistered`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Push Permissions (Web Chat)
   * Pushes permissions for webchat
   * @returns WebChatPermissionsViewModel Success
   * @throws ApiError
   */
  public static async pushWebChatPermissions(
    {
      body,
    }: {
      body?: WebChatPermissionsRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<WebChatPermissionsViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<WebChatPermissionsViewModel>({
      method: 'POST',
      url: `/v2/users/push/webchat`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * My User
   * Gets the my users
   * @returns MyUserGetViewModel Success
   * @throws ApiError
   */
  public static async getMyUsers(
    {
      authorization,
    }: {
      /**
       * Authorization
       */
      authorization: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<MyUserGetViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<Array<MyUserGetViewModel>>({
      method: 'GET',
      url: `/v2/users/myuser`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * My User
   * Creates the my users
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async createMyUsers(
    {
      authorization,
      body,
    }: {
      /**
       * Authorization
       */
      authorization: string;
      body?: MyUserUpsertViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'POST',
      url: `/v2/users/myuser`,
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * My User
   * Gets the my user by identifier
   * @returns MyUserGetViewModel Success
   * @throws ApiError
   */
  public static async getMyUserById(
    {
      authorization,
      id,
    }: {
      /**
       * Authorization
       */
      authorization: string;
      id: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<MyUserGetViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<MyUserGetViewModel>({
      method: 'GET',
      url: `/v2/users/myuser/${id}`,
      path: {
        ...(id ? { id: id } : {}),
      },
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * My User
   * Updates the my users
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async updateMyUsers(
    {
      authorization,
      id,
      body,
    }: {
      /**
       * Authorization
       */
      authorization: string;
      id: string;
      body?: MyUserUpsertViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'PUT',
      url: `/v2/users/myuser/${id}`,
      path: {
        ...(id ? { id: id } : {}),
      },
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * My User
   * Deletes the my users
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async deleteMyUsers(
    {
      authorization,
      id,
    }: {
      /**
       * Authorization
       */
      authorization: string;
      id: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'DELETE',
      url: `/v2/users/myuser/${id}`,
      path: {
        ...(id ? { id: id } : {}),
      },
      headers: {
        ...(authorization ? { authorization: authorization } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}

declare module '@setur/api/v2' {
  interface Services {
    UsersService: typeof UsersService;
  }
}
